/* =====================================================================
   HelalLife / OneCircle – Produktseiten-Logik
   Ausgelagert aus main-product.liquid (Performance: cachebar, defer)
   ---------------------------------------------------------------------
   Enthält:
   - Smart-Description Parser + Brand-Inferenz
   - Produktkarten-Merge, Facts-Verschiebung, Rücksendeadresse
   - Untere Tabs (Beschreibung / Produktdetails / Hinweise / Verkäufer)
   - Webkul-Integration: Seller-Title, Delivery-Tabelle, Policy, Inline-Farben strippen
   - 1 gebündelter, zeitlich begrenzter Observer statt 4 Dauer-Observer
   ===================================================================== */
(function () {
  'use strict';

  /* ===================================================================
     1) SMART DESCRIPTION PARSER
     =================================================================== */
  function helalLifeFormatDescriptionText(rawText) {
    if (!rawText) return '';

    const normalizeDisplayText = (value) =>
      (value || '')
        .replace(/\u00C3\u00A4/g, 'ä')
        .replace(/\u00C3\u201E/g, 'Ä')
        .replace(/\u00C3\u00B6/g, 'ö')
        .replace(/\u00C3\u2013/g, 'Ö')
        .replace(/\u00C3\u00BC/g, 'ü')
        .replace(/\u00C3\u0152/g, 'Ü')
        .replace(/\u00C3\u0178/g, 'ß')
        .replace(/\u00C2\u00B0/g, '°')
        .replace(/\u00E2\u20AC\u201C/g, '–')
        .replace(/\u00E2\u20AC\u201D/g, '—')
        .replace(/\u00E2\u20AC\u00A2/g, '•')
        .replace(/\u00C3\u00A9/g, 'é')
        .replace(/\u00C3\u00A8/g, 'è')
        .replace(/\u00C3\u0020/g, 'à')
        .replace(/\u00C3\u00A1/g, 'á')
        .replace(/\u00C3\u2014/g, 'x')
        .replace(/\b(\d+)\s(\d{1,2})\s(?=(?:kg|g|cm|mm|m|l|ml)\b)/gi, '$1,$2 ')
        .replace(/\b(\d+)\s(\d{1,2})\b(?=\s*x\s*\d)/gi, '$1,$2')
        .replace(/\b(\d+)\s*[xX]\s*(\d+)\b/g, '$1 x $2');

    const normalizeForMatch = (value) =>
      normalizeDisplayText(value)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ß/g, 'ss')
        .replace(/[^a-z0-9%°+&/ .,:\-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const escapeHtml = (text) =>
      (text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const normalizeSpacing = (value) =>
      normalizeDisplayText(value)
        .replace(/\r/g, '\n')
        .replace(/\u00a0/g, ' ')
        .replace(/[ \t]+/g, ' ')
        .replace(/\s*([,;:])\s*/g, '$1 ')
        .replace(/(\d),\s+(\d)/g, '$1,$2')
        .replace(/\s+([.)])/g, '$1')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

    const cleanLine = (value) =>
      normalizeSpacing(value)
        .replace(/^[•·●◦▪\-–—]+\s*/g, '')
        .replace(/^\*+\s*/g, '')
        .trim();

    const labelDefinitions = [
      /* Allgemein / Identifikation */
      { label: 'Marke', needles: ['Marke', 'Brand', 'Hersteller', 'Manufacturer'] },
      { label: 'Modell', needles: ['Modell', 'Model', 'Artikelnummer', 'Referenz', 'Reference', 'Typenbezeichnung', 'Typ'] },
      { label: 'Serie', needles: ['Serie', 'Kollektion', 'Collection', 'Produktlinie', 'Linie'] },
      { label: 'EAN / GTIN', needles: ['EAN', 'GTIN', 'Barcode', 'ISBN'] },
      { label: 'Garantie', needles: ['Garantie', 'Gewährleistung', 'Herstellergarantie', 'Warranty'] },
      { label: 'Zertifizierung', needles: ['Zertifizierung', 'Zertifikat', 'Prüfzeichen', 'CE', 'TÜV', 'GS', 'RoHS', 'ISO', 'Certification'] },
      { label: 'Herkunftsland', needles: ['Herkunftsland', 'Made in', 'Ursprungsland', 'Country of origin'] },

      /* Lieferung & Verpackung */
      { label: 'Lieferumfang', needles: ['Lieferumfang', 'Lieferung mit', 'Im Lieferumfang enthalten', 'Scope of delivery', 'Package contents', 'VPE', 'VE', 'Packungseinheit', 'Verpackungseinheit', 'Packungsinhalt'] },
      { label: 'Verpackungsart', needles: ['Verpackungsart', 'Verpackung', 'Packaging'] },
      { label: 'Versandgewicht', needles: ['Versandgewicht', 'Shipping weight'] },

      /* Physikalische Eigenschaften */
      { label: 'Material', needles: ['Material', 'Materialzusammensetzung', 'Material composition', 'Zusammensetzung', 'Werkstoff'] },
      { label: 'Farbe', needles: ['Farbe', 'Color', 'Colour', 'Farbton', 'Farbgebung'] },
      { label: 'Oberfläche', needles: ['Oberfläche', 'Oberflächenbeschaffenheit', 'Finish', 'Beschichtung'] },
      { label: 'Größe', needles: ['Größe', 'Groesse', 'Size', 'Konfektionsgröße', 'Kleidergröße'] },
      { label: 'Maße', needles: ['Maße', 'Masse', 'Dimensions', 'Abmessung', 'Abmessungen', 'Gesamtmaße', 'Außenmaße', 'Innenmaße'] },
      { label: 'Länge', needles: ['Länge', 'Laenge', 'Length', 'Gesamtlänge', 'Kabellänge'] },
      { label: 'Breite', needles: ['Breite', 'Width', 'Gesamtbreite'] },
      { label: 'Höhe', needles: ['Höhe', 'Hoehe', 'Height', 'Gesamthöhe'] },
      { label: 'Tiefe', needles: ['Tiefe', 'Depth', 'Tiefe gesamt'] },
      { label: 'Durchmesser', needles: ['Durchmesser', 'Diameter', 'Ø', 'Innen-Ø', 'Außen-Ø'] },
      { label: 'Wandstärke', needles: ['Wandstärke', 'Wanddicke', 'Dicke', 'Stärke', 'Thickness'] },
      { label: 'Gewicht', needles: ['Gewicht', 'Weight', 'Eigengewicht', 'Netto-Gewicht', 'Bruttogewicht'] },
      { label: 'Belastbarkeit', needles: ['Belastbarkeit', 'Maximalbelastung', 'Traglast', 'Max. Belastung', 'Max. Gewicht', 'Max. Zuglast', 'Load capacity'] },
      { label: 'Inhalt', needles: ['Inhalt', 'Volumen', 'Volume', 'Fassungsvermögen', 'Nennvolumen', 'Füllmenge'] },
      { label: 'Menge', needles: ['Menge', 'Stückzahl', 'Anzahl', 'Packungsinhalt', 'Inhalt pro Packung', 'Stück', 'Rollen', 'Blatt'] },

      /* Elektrik / Elektronik */
      { label: 'Leistung', needles: ['Leistung', 'Power', 'Watt', 'Wattzahl', 'Nennleistung', 'Maximalleistung'] },
      { label: 'Spannung', needles: ['Spannung', 'Voltage', 'Nennspannung', 'Betriebsspannung', 'Volt'] },
      { label: 'Stromstärke', needles: ['Stromstärke', 'Ampere', 'Strom', 'Current'] },
      { label: 'Frequenz', needles: ['Frequenz', 'Frequency', 'Hz'] },
      { label: 'Energieklasse', needles: ['Energieklasse', 'Energieeffizienzklasse', 'Energy class', 'Effizienzklasse'] },
      { label: 'Kapazität', needles: ['Kapazität', 'Kapazitaet', 'Capacity', 'Akkukapazität', 'Batteriekapazität', 'mAh', 'Wh'] },
      { label: 'Akkulaufzeit', needles: ['Akkulaufzeit', 'Laufzeit', 'Batterielaufzeit', 'Battery life', 'Standby', 'Betriebsdauer'] },
      { label: 'Ladezeit', needles: ['Ladezeit', 'Charging time'] },
      { label: 'Batterietyp', needles: ['Batterietyp', 'Batterie', 'Akkutyp', 'Battery type'] },
      { label: 'Prozessor', needles: ['Prozessor', 'CPU', 'Chip', 'Chipset'] },
      { label: 'Arbeitsspeicher', needles: ['Arbeitsspeicher', 'RAM', 'Memory'] },
      { label: 'Speicher', needles: ['Speicher', 'Speicherkapazität', 'Storage', 'SSD', 'HDD', 'Flash-Speicher', 'Interner Speicher', 'Externer Speicher'] },
      { label: 'Display', needles: ['Display', 'Bildschirm', 'Screen', 'Panel', 'Monitor'] },
      { label: 'Auflösung', needles: ['Auflösung', 'Resolution', 'Pixel', 'Display-Auflösung'] },
      { label: 'Bildwiederholrate', needles: ['Bildwiederholrate', 'Refresh rate', 'Hz Display'] },
      { label: 'Konnektivität', needles: ['Konnektivität', 'Schnittstellen', 'Connectivity', 'Anschlüsse', 'Ports'] },
      { label: 'WLAN', needles: ['WLAN', 'Wi-Fi', 'WiFi', 'Wireless'] },
      { label: 'Bluetooth', needles: ['Bluetooth', 'BT'] },
      { label: 'USB', needles: ['USB', 'USB-C', 'USB 3.0', 'USB 2.0'] },
      { label: 'Betriebssystem', needles: ['Betriebssystem', 'OS', 'Operating system', 'Software'] },
      { label: 'Kompatibilität', needles: ['Kompatibilität', 'Kompatibilitaet', 'Kompatibel mit', 'Compatible with', 'Passend für', 'Geeignet für'] },
      { label: 'Schutzklasse', needles: ['Schutzklasse', 'IP-Schutzart', 'Schutzart', 'IP67', 'IP68', 'IP65', 'IP54'] },
      { label: 'Schallpegel', needles: ['Schallpegel', 'Lautstärke', 'dB', 'Lärmpegel', 'Geräuschpegel'] },
      { label: 'Betriebstemperatur', needles: ['Betriebstemperatur', 'Einsatztemperatur', 'Operating temperature'] },

      /* Textilien / Bekleidung */
      { label: 'Passform', needles: ['Passform', 'Fit', 'Schnitt', 'Silhouette'] },
      { label: 'Stil', needles: ['Stil', 'Style', 'Design'] },
      { label: 'Verschluss', needles: ['Verschluss', 'Clasp', 'Reißverschluss', 'Druckknöpfe', 'Klettverschluss'] },
      { label: 'Pflegeanweisung', needles: ['Pflegeanweisung', 'Pflege', 'Care instructions', 'Pflegehinweise', 'Wäsche', 'Waschhinweis'] },
      { label: 'Saison', needles: ['Saison', 'Season', 'Jahreszeit'] },
      { label: 'Wassersäule', needles: ['Wassersäule', 'Waterproof', 'Waterproofing', 'mm Wassersäule'] },
      { label: 'Atmungsaktivität', needles: ['Atmungsaktivität', 'Atmungsaktiv', 'Breathable', 'Breathability'] },

      /* Lebensmittel / Ernährung */
      { label: 'Zutaten', needles: ['Zutaten', 'Inhaltsstoffe', 'Ingredients', 'Zusammensetzung'] },
      { label: 'Allergene', needles: ['Allergene', 'Allergiehinweise', 'Allergenhinweise', 'Allergens', 'Enthält'] },
      { label: 'Nährwerte', needles: ['Nährwerte', 'Nährwertangaben', 'Nutrition', 'Nutritional values'] },
      { label: 'Energie', needles: ['Energie', 'Kalorien', 'kcal', 'kJ', 'Brennwert', 'Calories'] },
      { label: 'Fett', needles: ['Fett', 'Fat', 'Davon gesättigte Fettsäuren', 'Gesättigte Fettsäuren', 'Fettsäuren'] },
      { label: 'Kohlenhydrate', needles: ['Kohlenhydrate', 'Carbohydrates', 'Davon Zucker', 'Zucker'] },
      { label: 'Eiweiß', needles: ['Eiweiß', 'Eiweiss', 'Protein', 'Proteine'] },
      { label: 'Ballaststoffe', needles: ['Ballaststoffe', 'Fibre', 'Fiber'] },
      { label: 'Salz', needles: ['Salz', 'Natrium', 'Salt', 'Sodium'] },
      { label: 'Haltbarkeit', needles: ['Mindesthaltbarkeitsdatum', 'MHD', 'Haltbarkeit', 'Best before', 'Verbrauchsdatum', 'Best by'] },
      { label: 'Lagerung', needles: ['Lagerung', 'Lagerhinweis', 'Storage', 'Aufbewahrung', 'Lagerbedingungen', 'Kühl lagern', 'Trocken lagern'] },
      { label: 'Hergestellt in', needles: ['Hergestellt in', 'Erzeugt in', 'Abgefüllt in', 'Verpackt in', 'Produced in'] },
      { label: 'Bio', needles: ['Bio', 'Organic', 'Ökologisch', 'EU-Bio', 'Demeter', 'Naturland', 'Bioland'] },
      { label: 'Diät', needles: ['Vegan', 'Vegetarisch', 'Glutenfrei', 'Laktosefrei', 'Zuckerfrei', 'Halal', 'Kosher', 'Keto'] },

      /* Kosmetik / Beauty */
      { label: 'Hauttyp', needles: ['Hauttyp', 'Skin type', 'Geeignet für Hauttyp', 'Passend für Hauttyp'] },
      { label: 'Anwendungsgebiet', needles: ['Anwendungsgebiet', 'Anwendungsbereich', 'Application area'] },
      { label: 'Wirkstoffe', needles: ['Wirkstoffe', 'Wirkstoff', 'Active ingredients', 'INCI'] },
      { label: 'Dosierung', needles: ['Dosierung', 'Dose', 'Dosage', 'Anwendungsmenge'] },
      { label: 'Anwendung', needles: ['Anwendung', 'Usage', 'Anwendungshinweise', 'Verwendung', 'Auftragen', 'Application'] },
      { label: 'Füllmenge', needles: ['Füllmenge', 'Inhaltsmenge', 'Net weight', 'Nettofüllmenge', 'ml', 'g Inhalt'] },

      /* Möbel / Einrichtung */
      { label: 'Montagematerial', needles: ['Montagematerial', 'Montagehinweise', 'Aufbauanleitung', 'Befestigungsmaterial', 'Schrauben enthalten'] },
      { label: 'Anzahl Teile', needles: ['Anzahl Teile', 'Teilezahl', 'Teile enthalten', 'Number of parts'] },
      { label: 'Pflegehinweise', needles: ['Pflegehinweise', 'Reinigung', 'Reinigungshinweise', 'Cleaning instructions'] },

      /* Garten / Pflanzen */
      { label: 'Pflanzenart', needles: ['Pflanzenart', 'Pflanze', 'Plant type', 'Botanischer Name'] },
      { label: 'Standort', needles: ['Standort', 'Location', 'Sonnenbedarf', 'Lichtbedarf'] },
      { label: 'Substrat', needles: ['Substrat', 'Erde', 'Boden', 'Soil'] },
      { label: 'Wasserbedarf', needles: ['Wasserbedarf', 'Bewässerung', 'Giessen', 'Gießen', 'Watering'] },
      { label: 'Wuchshöhe', needles: ['Wuchshöhe', 'Wuchshoehe', 'Endhöhe', 'Endgröße', 'Plant height'] },

      /* Automotive */
      { label: 'Fahrzeugtyp', needles: ['Fahrzeugtyp', 'Fahrzeugart', 'Vehicle type', 'Fahrzeug'] },
      { label: 'Baujahr', needles: ['Baujahr', 'Year of manufacture', 'Modelljahr', 'Jahrgang'] },
      { label: 'Motortyp', needles: ['Motortyp', 'Motor', 'Engine type', 'Antriebsart'] },
      { label: 'OEM-Nummer', needles: ['OEM', 'OEM-Nummer', 'Originalnummer', 'Original part number'] },

      /* Uhren */
      { label: 'Wasserdichtigkeit', needles: ['Wasserdichtigkeit', 'Water resistant', 'Water resistance', 'Wasserdicht', 'ATM', 'bar'], defaultValue: 'Ja' },
      { label: 'Uhrwerk', needles: ['Uhrwerk', 'Werk', 'Movement', 'Kaliber', 'Laufwerk'] },
      { label: 'Glas', needles: ['Glas', 'Crystal', 'Saphirglas', 'Mineralglas', 'Acrylglas'] },
      { label: 'Gehäusematerial', needles: ['Gehäusematerial', 'Case material', 'Gehäuse'] },
      { label: 'Gehäusefarbe', needles: ['Gehäusefarbe', 'Case color'] },
      { label: 'Gehäusebreite', needles: ['Gehäusebreite', 'Case width'] },
      { label: 'Gehäusehöhe', needles: ['Gehäusehöhe', 'Gehäusehoehe', 'Case height'] },
      { label: 'Gehäusedurchmesser', needles: ['Gehäusedurchmesser', 'Case diameter'] },
      { label: 'Gehäusestärke', needles: ['Gehäusestärke', 'Gehäusestaerke', 'Case thickness'] },
      { label: 'Armbandmaterial', needles: ['Armbandmaterial', 'Band material', 'Bracelet material', 'Strap material'] },
      { label: 'Armbandfarbe', needles: ['Armbandfarbe', 'Band color', 'Bracelet color', 'Strap color'] },
      { label: 'Bandbreite', needles: ['Bandbreite', 'Bracelet width', 'Strap width', 'Anstoßbreite'] },
      { label: 'Zifferblattfarbe', needles: ['Zifferblattfarbe', 'Dial color', 'Zifferblatt'] },
      { label: 'Steinbesatz', needles: ['Steinbesatz', 'Besatz', 'Stone setting', 'Diamantbesatz'] },

      /* Sport / Outdoor */
      { label: 'Sportart', needles: ['Sportart', 'Sport', 'Einsatzbereich', 'Verwendungsbereich'] },
      { label: 'Spielerposition', needles: ['Spielerposition', 'Position', 'Player position'] },
      { label: 'Schwierigkeitsgrad', needles: ['Schwierigkeitsgrad', 'Difficulty', 'Level'] },

      /* Gesundheit / Medizin */
      { label: 'Wirkstoff', needles: ['Wirkstoff', 'Wirkstoffgehalt', 'Active substance', 'Wirksubstanz'] },
      { label: 'Einnahme', needles: ['Einnahme', 'Einnahmeempfehlung', 'Dosierungsempfehlung', 'Intake', 'Tagesdosis'] },
      { label: 'Kontraindikationen', needles: ['Kontraindikationen', 'Gegenanzeigen', 'Nicht anwenden bei', 'Contraindications'] },

      /* Allgemein */
      { label: 'Funktionen', needles: ['Funktionen', 'Functions', 'Features', 'Ausstattung', 'Besonderheiten'] },
      { label: 'Sicherheitshinweise', needles: ['Sicherheitshinweise', 'Warnhinweise', 'Achtung', 'Warnung', 'Safety information'] },
      { label: 'Hinweis', needles: ['Hinweis', 'Hinweise', 'Note', 'Important', 'Wichtig', 'Bitte beachten'] }
    ];

    const sectionDefinitions = [
      { mode: 'facts', needles: ['Technische Daten', 'Spezifikationen', 'Produktdetails', 'Details', 'Specifications', 'Technische Details', 'Produktspezifikation', 'Artikeldetails', 'Eigenschaften', 'Produkteigenschaften', 'Nährwertangaben', 'Nährwerte', 'Nutritional values', 'Nutritional information', 'Technische Angaben', 'Maße und Gewicht', 'Maße & Gewicht'] },
      { mode: 'highlights', needles: ['Highlights', 'Merkmale', 'Vorteile', 'Ihre Vorteile', 'Das bietet Ihnen', 'Das bieten wir', 'Produktmerkmale', 'Besonderheiten', 'Features', 'Ausstattungsmerkmale', 'Das macht das Produkt besonders', 'Warum dieses Produkt'] },
      { mode: 'notes', needles: ['Hinweise', 'Wichtige Hinweise', 'Sicherheitshinweise', 'Pflegehinweise', 'Warnhinweise', 'Bitte beachten', 'Achtung', 'Rechtliche Hinweise', 'Haftungsausschluss', 'Disclaimer', 'Anmerkungen'] }
    ];

    const ignoredLinePatterns = [
      /^\s*$/,
      /^produktbeschreibung\s*:?\s*$/i,
      /^beschreibung\s*:?\s*$/i,
      /^details\s*:?\s*$/i,
      /^technische daten\s*:?\s*$/i,
      /^spezifikationen\s*:?\s*$/i,
      /^produktdetails\s*:?\s*$/i,
      /^artikeldetails\s*:?\s*$/i,
      /^eigenschaften\s*:?\s*$/i,
      /^highlights\s*:?\s*$/i,
      /^features\s*:?\s*$/i,
      /^vorteile\s*:?\s*$/i,
      /^merkmale\s*:?\s*$/i,
      /^lieferumfang\s*:?\s*$/i,
      /^nährwert(?:angaben|werte|tabelle)?\s*:?\s*$/i,
      /^hinweis(?:e)?\s*:?\s*$/i,
      /^sicherheitshinweis(?:e)?\s*:?\s*$/i,
      /^je\s+100\s*(?:ml|g)\s*:?\s*$/i,
      /^pro\s+(?:100\s*(?:ml|g)|portion|tablette|kapsel)\s*:?\s*$/i
    ];

    const noteLinePatterns = [
      /^(hinweis|hinweise|achtung|warnung|wichtig|bitte beachten)\b/i,
      /^informationen über den verkäufer\b/i,
      /^aus umweltschutzgründen\b/i,
      /^bedienungsanleitungen?\b/i,
      /seller policy/i,
      /^dies(?:e[sr]?)?\s+(?:produkt|artikel)\s+(?:enthält|kann enthalten)/i,
      /^für kinder unter\b/i,
      /^außerhalb der reichweite von kindern\b/i,
      /^nicht für kinder\b/i,
      /^von kindern fernhalten\b/i,
      /^kühl und trocken lagern\b/i,
      /^vor sonnenlicht schützen\b/i,
      /^bei raumtemperatur lagern\b/i,
      /^haftungsausschluss\b/i,
      /^rechtliche hinweise\b/i,
      /^disclaimer\b/i
    ];

    const featureOnlyMap = [
      { pattern: /^water resistant\.?$/i, label: 'Wasserdichtigkeit', value: 'Ja' },
      { pattern: /^wasserdicht\.?$/i, label: 'Wasserdichtigkeit', value: 'Ja' },
      { pattern: /^batteriebetrieben\.?$/i, label: 'Betriebsart', value: 'Batteriebetrieben' },
      { pattern: /^solarbetrieben\.?$/i, label: 'Betriebsart', value: 'Solarbetrieben' },
      { pattern: /^automatik\.?$/i, label: 'Uhrwerk', value: 'Automatik' },
      { pattern: /^quarz(?:uhrwerk)?\.?$/i, label: 'Uhrwerk', value: 'Quarz' },
      { pattern: /^vegan\.?$/i, label: 'Diät', value: 'Vegan' },
      { pattern: /^vegetarisch\.?$/i, label: 'Diät', value: 'Vegetarisch' },
      { pattern: /^glutenfrei\.?$/i, label: 'Diät', value: 'Glutenfrei' },
      { pattern: /^laktosefrei\.?$/i, label: 'Diät', value: 'Laktosefrei' },
      { pattern: /^zuckerfrei\.?$/i, label: 'Diät', value: 'Zuckerfrei' },
      { pattern: /^halal\.?$/i, label: 'Diät', value: 'Halal' },
      { pattern: /^bio(?:logisch)?\.?$/i, label: 'Bio', value: 'Ja' },
      { pattern: /^organic\.?$/i, label: 'Bio', value: 'Ja' },
      { pattern: /^kabellos\.?$/i, label: 'Konnektivität', value: 'Kabellos' },
      { pattern: /^wiederaufladbar\.?$/i, label: 'Batterietyp', value: 'Wiederaufladbar' },
      { pattern: /^faltbar\.?$/i, label: 'Besonderheit', value: 'Faltbar' },
      { pattern: /^klappbar\.?$/i, label: 'Besonderheit', value: 'Klappbar' },
      { pattern: /^spülmaschinenfest\.?$/i, label: 'Pflegehinweise', value: 'Spülmaschinenfest' },
      { pattern: /^spülmaschinengeeignet\.?$/i, label: 'Pflegehinweise', value: 'Spülmaschinengeeignet' },
      { pattern: /^mikrowellengeeignet\.?$/i, label: 'Pflegehinweise', value: 'Mikrowellengeeignet' },
      { pattern: /^atmungsaktiv\.?$/i, label: 'Atmungsaktivität', value: 'Ja' },
      { pattern: /^maschinenwaschbar\.?$/i, label: 'Pflegehinweise', value: 'Maschinenwaschbar' },
      { pattern: /^handwaschbar\.?$/i, label: 'Pflegehinweise', value: 'Handwaschbar' },
      { pattern: /^uv-schutz\.?$/i, label: 'UV-Schutz', value: 'Ja' },
      { pattern: /^frostbeständig\.?$/i, label: 'Besonderheit', value: 'Frostbeständig' },
      { pattern: /^recycelt\.?$/i, label: 'Material', value: 'Recycelt' },
      { pattern: /^nachhaltig\.?$/i, label: 'Nachhaltigkeit', value: 'Ja' }
    ];

    const allNeedles = labelDefinitions
      .flatMap((definition) => definition.needles.map((needle) => ({ definition, needle, normalized: normalizeForMatch(needle) })))
      .sort((a, b) => b.normalized.length - a.normalized.length);

    const allSections = sectionDefinitions
      .flatMap((definition) => definition.needles.map((needle) => ({ mode: definition.mode, normalized: normalizeForMatch(needle) })))
      .sort((a, b) => b.normalized.length - a.normalized.length);

    const labelAlternation = [...new Set(allNeedles.map(({ needle }) => needle).filter(Boolean))]
      .sort((a, b) => b.length - a.length)
      .map(escapeRegex)
      .join('|');

    const inlineLabelBreakPattern = labelAlternation
      ? new RegExp(`([.!?;|])\\s+(?=(?:${labelAlternation})\\b)|\\s*[-–—]\\s+(?=(?:${labelAlternation})\\b)|\\s+(?=(?:${labelAlternation})\\s*[:\\-–—])`, 'gi')
      : null;

    const matchesNeedleExactly = (line, needle) => {
      const normalizedLine = normalizeForMatch(line).replace(/[:\-–—.]$/g, '').trim();
      return normalizedLine === normalizeForMatch(needle);
    };

    const parseFactLine = (line) => {
      const raw = cleanLine(line);
      if (!raw) return null;

      for (const mapped of featureOnlyMap) {
        if (mapped.pattern.test(raw)) {
          return { label: mapped.label, value: mapped.value };
        }
      }

      for (const { definition, needle } of allNeedles) {
        const escapedNeedle = escapeRegex(needle);
        const labelWithValuePattern = new RegExp(`^${escapedNeedle}\\s*(?:[:\\-–—]\\s*|\\s+)(.+)$`, 'i');
        const match = raw.match(labelWithValuePattern);

        if (match) {
          const value = match[1].replace(/^[:\-–—\s]+/, '').replace(/[\s.]+$/, '').trim();
          if (value && !/^(ca\.?|circa|approx\.?|approx|etwa)$/i.test(value)) {
            return { label: definition.label, value };
          }
        }

        if (matchesNeedleExactly(raw, needle) && definition.defaultValue) {
          return { label: definition.label, value: definition.defaultValue };
        }
      }

      if (/^lieferung mit\b/i.test(raw)) {
        const value = raw.replace(/^lieferung mit\s*/i, '').replace(/[\s.]+$/, '').trim();
        if (value) return { label: 'Lieferumfang', value };
      }

      return null;
    };

    const getLabelOnlyInfo = (line) => {
      const raw = cleanLine(line);
      if (!raw) return null;

      for (const { definition, needle } of allNeedles) {
        const pattern = new RegExp(`^${escapeRegex(needle)}\\s*(?::)?\\s*(ca\\.?|circa|approx\\.?|approx|etwa)?\\s*$`, 'i');
        const match = raw.match(pattern);
        if (match) {
          return {
            label: definition.label,
            carry: match[1] ? match[1].replace(/\.$/, '') : ''
          };
        }
      }

      return null;
    };

    const looksLikeStandaloneValue = (line) => {
      const raw = cleanLine(line);
      if (!raw) return false;

      if (/^((ca\.?|circa|approx\.?|approx|etwa)\s+)?\d+[\d.,\s/x×-]*(mm|cm|m|kg|g|mg|ml|l|bar|atm|v|volt|w|watt|mah|wh|hz|zoll|inch|"|')?$/i.test(raw)) return true;
      if (/^[A-Za-zÄÖÜäöüß0-9][A-Za-zÄÖÜäöüß0-9\-\/., ]{0,24}$/.test(raw) && !/[.!?]$/.test(raw)) return true;
      return false;
    };

    const splitComplexLine = (line) => {
      const raw = cleanLine(line);
      if (!raw) return [];

      if (raw.includes(';')) {
        return raw.split(/\s*;\s*/).map(cleanLine).filter(Boolean);
      }

      return [raw];
    };

    const formatMeasurementValue = (value) =>
      cleanLine(value)
        .replace(/(\d),\s+(\d)/g, '$1,$2')
        .replace(/(\d)(mm|cm|m|ml|l|g\/m²|g\/m2|my|kg|mg)\b/gi, '$1 $2')
        .replace(/\s*(x|×)\s*/gi, ' x ')
        .replace(/(Ø|ø)\s*/g, 'Ø ')
        .replace(/\s{2,}/g, ' ')
        .trim();

    const inlineDataExtractors = [
      {
        label: 'Format',
        pattern: /\b\d+(?:[.,]\d+)?\s*(?:m|cm|mm)\s+\d+(?:[.,]\d+)?\s*(?:cm|mm)\b/gi,
        formatter: (value) => formatMeasurementValue(value).replace(/((?:mm|cm|m))\s+(?=\d)/gi, '$1 x ')
      },
      {
        label: 'Format',
        pattern: /\b(?:\d+(?:[.,]\d+)?\s*(?:mm|cm|m)?\s*x\s*){1,3}(?:Ø|ø)?\s*\d+(?:[.,]\d+)?\s*(?:mm|cm|m)\b/gi,
        formatter: (value) => formatMeasurementValue(value)
      },
      {
        label: 'Durchmesser',
        pattern: /(?:Ø|ø)\s*\d+(?:[.,]\d+)?\s*(?:mm|cm|m)\b/gi,
        formatter: (value) => formatMeasurementValue(value).replace(/^Ø\s*/i, '')
      },
      {
        label: 'Breite',
        pattern: /\b[a-zäöüß-]*breite\s*\d+(?:[.,]\d+)?\s*(?:mm|cm|m)\b/gi,
        formatter: (value) => formatMeasurementValue(value).replace(/^[A-Za-zÄÖÜäöüß-]*breite\s*/i, '')
      },
      {
        label: 'Länge',
        pattern: /\b[a-zäöüß-]*länge\s*\d+(?:[.,]\d+)?\s*(?:mm|cm|m)\b/gi,
        formatter: (value) => formatMeasurementValue(value).replace(/^[A-Za-zÄÖÜäöüß-]*länge\s*/i, '')
      },
      {
        label: 'Höhe',
        pattern: /\b[a-zäöüß-]*(?:höhe|hoehe)\s*\d+(?:[.,]\d+)?\s*(?:mm|cm|m)\b/gi,
        formatter: (value) => formatMeasurementValue(value).replace(/^[A-Za-zÄÖÜäöüß-]*(?:höhe|hoehe)\s*/i, '')
      },
      {
        label: 'Breite',
        pattern: /\bB\s*\d+(?:[.,]\d+)?\s*(?:mm|cm|m)\b/gi,
        formatter: (value) => formatMeasurementValue(value).replace(/^B\s*/i, '')
      },
      {
        label: 'Länge',
        pattern: /\bL\s*\d+(?:[.,]\d+)?\s*(?:mm|cm|m)\b/gi,
        formatter: (value) => formatMeasurementValue(value).replace(/^L\s*/i, '')
      },
      {
        label: 'Höhe',
        pattern: /\bH\s*\d+(?:[.,]\d+)?\s*(?:mm|cm|m)\b/gi,
        formatter: (value) => formatMeasurementValue(value).replace(/^H\s*/i, '')
      },
      {
        label: 'Grammatur',
        pattern: /\b\d+(?:[.,]\d+)?\s*g\/m(?:²|2)(?=\s|[,.;]|$)/gi,
        formatter: (value) => formatMeasurementValue(value)
      },
      {
        label: 'Materialstärke',
        pattern: /\b\d+(?:[.,]\d+)?\s*my\b/gi,
        formatter: (value) => formatMeasurementValue(value)
      },
      {
        label: 'Maß',
        pattern: /\b\d+(?:[.,]\d+)?\s*(?:mm|cm)\b/gi,
        formatter: (value) => formatMeasurementValue(value)
      },
      {
        label: 'Länge',
        pattern: /\b\d+(?:[.,]\d+)?\s*m\b/gi,
        formatter: (value) => formatMeasurementValue(value)
      },
      {
        label: 'Inhalt',
        pattern: /\b\d+(?:[.,]\d+)?\s*(?:ml|l)\b/gi,
        formatter: (value) => formatMeasurementValue(value)
      },
      {
        label: 'Packungsinhalt',
        pattern: /\b\d+\s*x\s*\d+\s*Blatt\b/gi,
        formatter: (value) => cleanLine(value)
      },
      {
        label: 'Lieferumfang',
        pattern: /\b\d+\s*VPE\b/gi,
        formatter: (value) => cleanLine(value)
      },
      {
        label: 'Lieferumfang',
        pattern: /\bVE\b\s*=?\s*[A-Za-z0-9()\- ]+/gi,
        formatter: (value) => cleanLine(value).replace(/^VE\b\s*=?\s*/i, '')
      },
      {
        label: 'Menge',
        pattern: /\b\d+\s*(?:Stück|Stk\.?|Rollen|Blatt|Würfel|Satz|Set)\b/gi,
        formatter: (value) => cleanLine(value)
      },
      {
        label: 'Menge',
        pattern: /\b\d+\s*er\s*Satz\b/gi,
        formatter: (value) => cleanLine(value)
      },
      {
        label: 'Menge',
        pattern: /\b\d+\s*teilig(?:es)?\s*Set\b/gi,
        formatter: (value) => cleanLine(value)
      }
    ];

    const extractInlineDataFacts = (line) => {
      const extractedFacts = [];
      let remaining = ` ${cleanLine(line)} `;

      inlineDataExtractors.forEach((extractor) => {
        remaining = remaining.replace(extractor.pattern, (match) => {
          const value = extractor.formatter ? extractor.formatter(match) : cleanLine(match);
          if (value) {
            extractedFacts.push({ label: extractor.label, value });
          }
          return ' ';
        });
      });

      remaining = remaining
        .replace(/\s{2,}/g, ' ')
        .replace(/\s+([,;:.])/g, '$1')
        .replace(/([,;:])(?=[^ ])/g, '$1 ')
        .replace(/^[,;:\-–—\s]+|[,;:\-–—\s]+$/g, '')
        .trim();

      return {
        remainingText: remaining,
        facts: extractedFacts
      };
    };

    const mergeContinuationLines = (lines) => {
      const merged = [];

      for (let index = 0; index < lines.length; index += 1) {
        const current = lines[index];
        const next = lines[index + 1] ? cleanLine(lines[index + 1]) : '';
        const labelInfo = getLabelOnlyInfo(current);

        if (labelInfo && next && looksLikeStandaloneValue(next)) {
          const prefix = labelInfo.carry ? `${labelInfo.carry}. `.replace(/\.\s$/, '. ') : '';
          merged.push(`${labelInfo.label}: ${prefix}${next}`.replace(/\s+/g, ' ').trim());
          index += 1;
          continue;
        }

        if (/\b(ca\.?|circa|approx\.?|approx|etwa)$/i.test(current) && next && looksLikeStandaloneValue(next)) {
          merged.push(`${current} ${next}`.replace(/\s+/g, ' ').trim());
          index += 1;
          continue;
        }

        merged.push(current);
      }

      return merged;
    };

    const getSectionMode = (line) => {
      const normalizedLine = normalizeForMatch(line.replace(/[:\-–—]+$/g, ''));
      const section = allSections.find((entry) => normalizedLine === entry.normalized);
      return section ? section.mode : '';
    };

    const isNoteLine = (line) => noteLinePatterns.some((pattern) => pattern.test(cleanLine(line)));

    const isLikelyFeatureLine = (line) => {
      const raw = cleanLine(line);
      if (!raw) return false;
      if (parseFactLine(raw)) return false;
      if (isNoteLine(raw)) return false;

      const wordCount = raw.split(/\s+/).filter(Boolean).length;
      const normalizedLine = normalizeForMatch(raw);
      const hasFeatureKeywords = /(edelstahl|leder|quarz|automatik|batterie|solar|stein|vergoldet|poliert|verschluss|armband|gehause|zifferblatt|display|kompatibel|kabellos|wiederaufladbar|wasserfest|wasserdicht)/i.test(normalizedLine);

      if (/^mit\b/i.test(raw) && wordCount <= 8) return true;
      if (/,/.test(raw) && wordCount <= 14) return true;
      if (hasFeatureKeywords && wordCount <= 14) return true;
      if (wordCount <= 4) return true;

      return false;
    };

    let normalized = normalizeSpacing(rawText)
      .replace(/(?:•|·|●|◦|▪)\s*/g, '\n• ')
      .replace(/\s*<->\s*/g, ' – ')
      .trim();

    if (inlineLabelBreakPattern) {
      normalized = normalized.replace(inlineLabelBreakPattern, (match, punctuation) => (
        punctuation ? `${punctuation}\n` : '\n'
      ));
    }

    if (!normalized) return '';

    const rawLines = normalized
      .split(/\n+/)
      .flatMap(splitComplexLine)
      .map(cleanLine)
      .filter((line) => line && !ignoredLinePatterns.some((pattern) => pattern.test(line)));

    const lines = mergeContinuationLines(rawLines);
    const intro = [];
    const highlights = [];
    const facts = [];
    const notes = [];
    const seen = new Set();
    let activeMode = '';

    const pushUnique = (target, value) => {
      const key = `${target}:${normalizeForMatch(typeof value === 'string' ? value : `${value.label} ${value.value}`)}`;
      if (seen.has(key)) return;
      seen.add(key);

      if (target === 'intro') intro.push(value);
      if (target === 'highlights') highlights.push(value);
      if (target === 'facts') facts.push(value);
      if (target === 'notes') notes.push(value);
    };

    lines.forEach((line) => {
      const sectionMode = getSectionMode(line);
      if (sectionMode) {
        activeMode = sectionMode;
        return;
      }

      if (isNoteLine(line)) {
        pushUnique('notes', line);
        return;
      }

      const fact = parseFactLine(line);
      if (fact && fact.value) {
        activeMode = 'facts';
        pushUnique('facts', fact);
        return;
      }

      if (activeMode === 'notes') {
        pushUnique('notes', line);
        return;
      }

      const { remainingText, facts: inlineFacts } = extractInlineDataFacts(line);
      if (inlineFacts.length) {
        activeMode = 'facts';
        inlineFacts.forEach((factItem) => pushUnique('facts', factItem));
      }

      if (!remainingText) {
        return;
      }

      if (getLabelOnlyInfo(remainingText)) {
        return;
      }

      if (!intro.length && activeMode !== 'highlights' && remainingText.split(/\s+/).filter(Boolean).length >= 3) {
        pushUnique('intro', remainingText);
        return;
      }

      if (activeMode === 'highlights' || isLikelyFeatureLine(remainingText)) {
        pushUnique('highlights', remainingText);
        return;
      }

      pushUnique('intro', remainingText);
    });

    const highlightMarkup = highlights
      .map((item) => `<li>${escapeHtml(item.replace(/[\s.]+$/, '').trim())}</li>`)
      .join('');

    const factMarkup = facts
      .map((fact) => `<li><strong>${escapeHtml(fact.label)}</strong><span class="hl-fact-value">${escapeHtml(fact.value)}</span></li>`)
      .join('');

    const sections = [
      intro.length ? `<div class="hl-desc-section hl-desc-section--intro"><div class="hl-desc-intro">${intro.map((item) => `<p>${escapeHtml(item)}</p>`).join('')}</div></div>` : '',
      highlights.length ? `<div class="hl-desc-section hl-desc-section--highlights"><h4 class="hl-desc-heading">Highlights</h4><ul class="hl-desc-highlights">${highlightMarkup}</ul></div>` : '',
      facts.length ? `<div class="hl-desc-section hl-desc-section--facts"><h4 class="hl-desc-heading">Produktdetails</h4><ul class="hl-desc-facts">${factMarkup}</ul></div>` : '',
      notes.length ? `<div class="hl-desc-section hl-desc-section--notes"><h4 class="hl-desc-heading">Hinweise</h4><div class="hl-desc-note">${notes.map((item) => `<p>${escapeHtml(item)}</p>`).join('')}</div></div>` : ''
    ].filter(Boolean).join('');

    return sections ? `<div class="hl-desc-card">${sections}</div>` : '';
  }

  function helalLifeBeautifyDescriptions(root) {
    root = root || document;
    root.querySelectorAll('.hl-smart-description[data-description-style="smart"]').forEach((container) => {
      if (container.dataset.descriptionFormatted === 'true') return;

      const originalHtml = (container.innerHTML || '').trim();
      if (!originalHtml) {
        container.dataset.descriptionFormatted = 'true';
        return;
      }

      const hasStructuredHtml = /<(p|ul|ol|table|h[1-6]|blockquote|details|summary)\b/i.test(originalHtml);
      if (hasStructuredHtml) {
        container.innerHTML = `<div class="hl-desc-card hl-desc-card--raw">${originalHtml}</div>`;
        container.dataset.descriptionFormatted = 'true';
        return;
      }

      const sourceHtml = originalHtml
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|li|ul|ol|section|article|h[1-6]|tr)>/gi, '\n')
        .replace(/<\/td>\s*<td[^>]*>/gi, ': ');

      const helper = document.createElement('div');
      helper.innerHTML = sourceHtml;

      const text = (helper.textContent || helper.innerText || '')
        .replace(/\u00a0/g, ' ')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n[ \t]+/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

      if (!text) {
        container.dataset.descriptionFormatted = 'true';
        return;
      }

      const formatted = helalLifeFormatDescriptionText(text);
      /* Sicherheitsnetz: erkennt der Formatter keine Struktur (Highlights/
         Produktdetails/Hinweise), bleibt die Originalbeschreibung erhalten –
         niemals leer rendern, damit kein Inhalt verloren geht. */
      container.innerHTML = formatted || `<div class="hl-desc-card hl-desc-card--raw">${originalHtml}</div>`;
      container.dataset.descriptionFormatted = 'true';
    });
  }

  /* ===================================================================
     2) BRAND-INFERENZ
     =================================================================== */
  function helalLifeInferBrandLabel(currentBrand, title, description, manufacturer) {
    const normalizeDisplayText = (value) =>
      (value || '')
        .replace(/\u00C3\u00A4/g, 'ä')
        .replace(/\u00C3\u201E/g, 'Ä')
        .replace(/\u00C3\u00B6/g, 'ö')
        .replace(/\u00C3\u2013/g, 'Ö')
        .replace(/\u00C3\u00BC/g, 'ü')
        .replace(/\u00C3\u0152/g, 'Ü')
        .replace(/\u00C3\u0178/g, 'ß')
        .replace(/\u00E2\u20AC\u201C/g, '–')
        .replace(/\u00E2\u20AC\u201D/g, '—')
        .replace(/\u00C3\u00A9/g, 'é')
        .replace(/\u00C3\u00A8/g, 'è')
        .replace(/\u00C3\u0020/g, 'à')
        .replace(/\u00C3\u00A1/g, 'á')
        .replace(/\s+/g, ' ')
        .trim();

    const normalizeKey = (value) =>
      normalizeDisplayText(value)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ß/g, 'ss')
        .replace(/[^a-z0-9&+./ -]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const genericBreakWords = new Set(['ab', 'aus', 'bei', 'der', 'die', 'das', 'ein', 'eine', 'fuer', 'für', 'inkl', 'mit', 'ohne', 'pack', 'set', 'und', 'vpe', 've']);
    const genericStopWords = new Set(['abdeckkappen', 'abmessung', 'acryl', 'adm', 'artikel', 'band', 'banner', 'beutel', 'block', 'brillenetui', 'classic', 'durchmesser', 'ec-cash', 'etui', 'filter', 'folie', 'form', 'geschenkgutschein', 'geschenkpapier', 'gluehspirale', 'glühspirale', 'halter', 'heizoelkanne', 'heizölkanne', 'hilfe', 'inhalt', 'kanne', 'kappe', 'kappen', 'kassenzettel', 'kassenrolle', 'kitt', 'klebeband', 'kleber', 'kohlefilter', 'kunststoff', 'laenge', 'länge', 'masse', 'mass', 'massband', 'menge', 'messer', 'modul', 'papier', 'pinsel', 'produkt', 'rahmen', 'reiniger', 'rolle', 'satz', 'schale', 'schnur', 'set', 'spirale', 'stueck', 'stück', 'tasche', 'teil', 'vpe', 've', 'weiss', 'weiß', 'würfel']);
    const genericSuffixes = ['band', 'beutel', 'block', 'etui', 'filter', 'folie', 'gutschein', 'halter', 'hilfe', 'kanne', 'kappe', 'kappen', 'kitt', 'kleber', 'papier', 'rolle', 'schnur', 'spirale', 'tasche'];
    const knownAcronyms = new Set(['3M', 'AEG', 'BMW', 'HP', 'LG', 'PKM', 'RE', 'WMF']);

    const softenBrandCasing = (value) =>
      normalizeDisplayText(value)
        .split(/\s+/)
        .map((part) => {
          const trimmed = part.trim();
          if (!trimmed) return '';
          const upper = trimmed.toUpperCase();
          if (knownAcronyms.has(upper)) return upper;
          if (/^[A-ZÄÖÜ0-9-]{3,}$/.test(trimmed)) {
            return trimmed.charAt(0) + trimmed.slice(1).toLowerCase();
          }
          return trimmed;
        })
        .filter(Boolean)
        .join(' ');

    const isUsableCandidate = (value) => {
      const candidate = normalizeDisplayText(value).replace(/^[,;:()[\]{}.\- ]+|[,;:()[\]{}.\- ]+$/g, '');
      if (candidate.length <= 1) return false;
      if (!/[A-Za-zÄÖÜäöüß]/.test(candidate)) return false;
      const lower = normalizeKey(candidate);
      if (!lower) return false;
      if (genericBreakWords.has(lower) || genericStopWords.has(lower)) return false;
      if (/^(?:\d+(?:[.,]\d+)?)(?:mm|cm|m|ml|l|kg|g|w|v|mah)?$/.test(lower)) return false;
      if (/^\d+(?:[.,]\d+)?x\d+(?:[.,]\d+)?(?:x\d+(?:[.,]\d+)?)?(?:mm|cm|m)?$/.test(lower)) return false;
      if (genericSuffixes.some((suffix) => lower.endsWith(suffix))) return false;
      return true;
    };

    const normalizeCandidateToken = (token) =>
      normalizeDisplayText(token)
        .replace(/^[^A-Za-zÄÖÜäöüß0-9]+|[^A-Za-zÄÖÜäöüß0-9-]+$/g, '')
        .replace(/-(?:VPE|VE)$/i, '');

    const collectCandidates = (value, limit) => {
      const source = normalizeDisplayText(value).replace(/[\r\n]+/g, ' ');
      if (!source) return [];
      const rawTokens = source.split(/\s+/).filter(Boolean).slice(0, limit);
      const results = [];
      rawTokens.forEach((token, index) => {
        const cleaned = normalizeCandidateToken(token);
        if (!cleaned || !isUsableCandidate(cleaned)) return;
        results.push({ token: cleaned, index });
      });
      return results;
    };

    const explicitBrand = normalizeDisplayText(currentBrand);
    if (explicitBrand && isUsableCandidate(explicitBrand)) {
      return softenBrandCasing(explicitBrand);
    }

    const manufacturerBrand = normalizeDisplayText(manufacturer);
    if (manufacturerBrand && isUsableCandidate(manufacturerBrand)) {
      return softenBrandCasing(manufacturerBrand);
    }

    const titleCandidates = collectCandidates(title, 18);
    const descriptionCandidates = collectCandidates(description, 28);
    if (!titleCandidates.length && !descriptionCandidates.length) return '';
    const scores = new Map();

    titleCandidates.forEach(({ token, index }) => {
      const key = normalizeKey(token);
      if (!key) return;
      const entry = scores.get(key) || {
        token,
        score: 0,
        titleSeen: false,
        descriptionSeen: false,
        bestIndex: 999,
        bestTitleIndex: 999,
        bestDescriptionIndex: 999
      };
      let score = entry.score || 0;
      entry.titleSeen = true;
      if (index <= 1) score += 5;
      else if (index <= 3) score += 3;
      else score += 1;
      if (/^[A-ZÄÖÜ0-9-]{3,}$/.test(token) || /^[A-ZÄÖÜ][A-Za-zÄÖÜäöüß-]+$/.test(token)) score += 3;
      if (/[-ÄÖÜäöüß]/.test(token)) score += 1;
      entry.score = score;
      entry.bestIndex = Math.min(entry.bestIndex, index);
      entry.bestTitleIndex = Math.min(entry.bestTitleIndex, index);
      scores.set(key, entry);
    });

    descriptionCandidates.forEach(({ token, index }) => {
      const key = normalizeKey(token);
      if (!key) return;
      const entry = scores.get(key) || {
        token,
        score: 0,
        titleSeen: false,
        descriptionSeen: false,
        bestIndex: 999,
        bestTitleIndex: 999,
        bestDescriptionIndex: 999
      };
      let score = entry.score || 0;
      entry.descriptionSeen = true;
      if (index <= 2) score += 2;
      else if (index <= 6) score += 1;
      if (/^[A-ZÄÖÜ0-9-]{3,}$/.test(token) || /^[A-ZÄÖÜ][A-Za-zÄÖÜäöüß-]+$/.test(token)) score += 1;
      entry.score = score;
      entry.bestIndex = Math.min(entry.bestIndex, index);
      entry.bestDescriptionIndex = Math.min(entry.bestDescriptionIndex, index);
      scores.set(key, entry);
    });

    let bestEntry = null;
    let bestScore = 0;
    scores.forEach((entry) => {
      let score = entry.score || 0;
      if (entry.titleSeen && entry.descriptionSeen) score += 6;
      if (score > bestScore) {
        bestEntry = { ...entry, score };
        bestScore = score;
      }
    });

    if (!bestEntry || bestScore < 8) return '';

    const strongSignal =
      /^[A-ZÄÖÜ0-9-]{3,}$/.test(bestEntry.token) ||
      bestEntry.bestTitleIndex === 0 ||
      bestEntry.bestDescriptionIndex === 0 ||
      /[-ÄÖÜäöüß]/.test(bestEntry.token);

    if (!(bestEntry.titleSeen && bestEntry.descriptionSeen && strongSignal)) return '';
    return softenBrandCasing(bestEntry.token);
  }

  function helalLifeRepairBrandLabels(root) {
    root = root || document;
    root.querySelectorAll('.hl-brand-line').forEach((line) => {
      const valueEl = line.querySelector('.hl-brand-value');
      if (!valueEl) return;

      const inferredBrand = helalLifeInferBrandLabel(
        line.dataset.brandOriginal || valueEl.textContent || '',
        line.dataset.productTitle || '',
        line.dataset.productDescription || '',
        line.dataset.manufacturer || ''
      );

      if (!inferredBrand) {
        valueEl.textContent = '';
        line.style.display = 'none';
        return;
      }

      valueEl.textContent = inferredBrand;
      line.style.display = '';
    });
  }

  /* ===================================================================
     3) PRODUKTKARTE / FACTS / RÜCKSENDEADRESSE
     =================================================================== */
  function helalLifeMergeProductCard(root) {
    root = root || document;

    const descContainer = root.querySelector('.hl-product-description');
    if (!descContainer) return;

    const specsInline = root.querySelector('.hl-product-specs-inline');
    const brandLine   = root.querySelector('.hl-brand-line');
    const sellerBox   = root.querySelector('#wk_seller_detail_title');

    /* Sicherstellen, dass eine Card existiert (auch bei reinen HTML-Beschreibungen) */
    let card = descContainer.querySelector('.hl-desc-card');
    if (!card) {
      card = document.createElement('div');
      card.className = 'hl-desc-card';
      descContainer.appendChild(card);
    }

    /* Facts-Sektion suchen oder anlegen */
    let factsSection = card.querySelector('.hl-desc-section--facts');
    let factsList;
    if (factsSection) {
      factsList = factsSection.querySelector('.hl-desc-facts');
    } else {
      factsSection = document.createElement('div');
      factsSection.className = 'hl-desc-section hl-desc-section--facts';
      factsSection.innerHTML = '<h4 class="hl-desc-heading">Produktdetails</h4><ul class="hl-desc-facts"></ul>';
      card.appendChild(factsSection);
      factsList = factsSection.querySelector('.hl-desc-facts');
    }

    /* Marke als erstes Fakten-Item einfügen */
    if (brandLine && brandLine.style.display !== 'none') {
      const brandVal = (brandLine.querySelector('.hl-brand-value') || {}).textContent;
      if (brandVal && brandVal.trim()) {
        const li = document.createElement('li');
        li.innerHTML = '<strong>Marke</strong><span class="hl-fact-value">' + brandVal.trim() + '</span>';
        factsList.insertBefore(li, factsList.firstChild);
      }
    }

    /* Metafeld-Spezifikationen anhängen */
    if (specsInline) {
      specsInline.querySelectorAll('.hl-spec-row').forEach(function(row) {
        const dt = (row.querySelector('dt') || {}).textContent || '';
        const dd = (row.querySelector('dd') || {}).textContent || '';
        if (dt.trim() && dd.trim()) {
          const li = document.createElement('li');
          const isAddress = /r.{0,4}cksendeadresse/i.test(dt);
          if (isAddress) {
            li.classList.add('hl-fact-address');
            /* Komma-getrennte Adresse in Zeilen aufteilen */
            const formatted = dd.trim().replace(/,\s*/g, '\n');
            li.innerHTML = '<strong>' + dt.trim() + '</strong><span class="hl-fact-value">' + formatted + '</span>';
          } else {
            li.innerHTML = '<strong>' + dt.trim() + '</strong><span class="hl-fact-value">' + dd.trim() + '</span>';
          }
          factsList.appendChild(li);
        }
      });
      specsInline.style.display = 'none';
    }

    /* Ursprüngliche Marken-Zeile ausblenden */
    if (brandLine) brandLine.style.display = 'none';

    /* Verkäufer-Info direkt nach dem Beschreibungsblock verschieben */
    if (sellerBox && descContainer.parentNode) {
      descContainer.parentNode.insertBefore(sellerBox, descContainer.nextSibling);
    }
  }

  /* Facts aus #longdesc in die Produktdetails-Kachel verschieben */
  function helalLifeMoveFacts() {
    var factsSection = document.querySelector('#longdesc .hl-desc-section--facts');
    var detailsPanel = document.getElementById('produktdetails');
    if (!detailsPanel) return;

    var card         = detailsPanel.querySelector('.hl-produktdetails-card');
    var noDetailsMsg = detailsPanel.querySelector('.hl-no-details-msg');
    var tabBtn       = document.querySelector('.hl-tab-btn[data-tab="produktdetails"]');

    if (factsSection) {
      if (card) card.appendChild(factsSection);
      /* Tab ausblenden wenn keine Fakten-Items vorhanden */
      var factItems = factsSection.querySelectorAll('.hl-desc-facts li');
      if (factItems.length === 0) {
        if (tabBtn) tabBtn.style.display = 'none';
        if (noDetailsMsg) noDetailsMsg.style.display = '';
      }
    } else {
      /* Keine Facts-Sektion → Tab verstecken */
      if (tabBtn) tabBtn.style.display = 'none';
      if (noDetailsMsg) noDetailsMsg.style.display = '';
    }
  }

  /* Rücksendeadresse aus Facts in Produkthinweise kopieren
     (Fallback für Produkte ohne Liquid-Metafeld, z.B. per Webkul Bulk-Edit) */
  function helalLifeShowReturnAddressInHinweise() {
    var jsPlaceholder = document.querySelector('#product_policy .hl-return-address-js');
    if (!jsPlaceholder) return;

    /* Wenn Liquid bereits eine Adresse gerendert hat → nichts tun */
    var liquidCard = document.querySelector('#product_policy .hl-return-address-card');
    if (liquidCard) return;

    /* Adresszeile aus den verschobenen Facts suchen */
    var addressItem = document.querySelector('#produktdetails .hl-desc-facts li.hl-fact-address')
                   || document.querySelector('#longdesc .hl-desc-facts li.hl-fact-address');
    if (!addressItem) return;

    var labelEl = addressItem.querySelector('strong');
    var valueEl = addressItem.querySelector('.hl-fact-value');
    if (!labelEl || !valueEl) return;

    var label = labelEl.textContent.replace(/:\s*$/, '').trim();
    var value = valueEl.textContent.trim();
    if (!value) return;

    jsPlaceholder.innerHTML =
      '<div class="hl-return-address-card">' +
        '<div class="hl-return-address-card-header">' +
          '<span class="hl-return-icon">↩</span>' +
          '<h4>' + label + '</h4>' +
        '</div>' +
        '<div class="hl-return-address-card-body">' +
          value.replace(/,\s*/g, '<br>') +
        '</div>' +
      '</div>';
    jsPlaceholder.style.display = '';

    /* Platzhalter-Text ausblenden, wenn Adresse vorhanden */
    var policyPlaceholder = document.querySelector('#product_policy .hl-policy-placeholder');
    var sellerPolicyTab = document.getElementById('seller-policy-tab');
    if (policyPlaceholder && sellerPolicyTab && sellerPolicyTab.children.length === 0) {
      policyPlaceholder.style.display = 'none';
    }
  }

  /* ===================================================================
     4) WEBKUL: INLINE-FARBEN STRIPPEN
     =================================================================== */
  var hlStripSelectors = [
    '#wk_seller_description',
    '#longdesc .hl-desc-intro',
    '#longdesc .hl-desc-card--raw'
  ];
  var hlIsStripping = false;

  function helalLifeStripInlineColors(rootSelector) {
    var root = typeof rootSelector === 'string'
      ? document.querySelector(rootSelector)
      : rootSelector;
    if (!root) return;
    hlIsStripping = true;
    root.querySelectorAll('*').forEach(function(el) {
      el.style.removeProperty('color');
      el.style.removeProperty('font-family');
      el.style.removeProperty('font-size');
      el.style.removeProperty('background');
      el.style.removeProperty('background-color');
    });
    root.querySelectorAll('font').forEach(function(el) {
      el.removeAttribute('color');
      el.removeAttribute('face');
    });
    hlIsStripping = false;
  }

  function hlStripAll() {
    hlStripSelectors.forEach(function(sel) {
      helalLifeStripInlineColors(sel);
    });
  }

  /* ===================================================================
     5) WEBKUL: DELIVERY-DIV IN SAUBERE TABELLE UMBAUEN
     =================================================================== */
  function helalLifeFormatDeliveryDiv() {
    var div = document.getElementById('wk_delivery_day_div');
    if (!div || div.dataset.hlFormatted) return;

    var rows = [];
    var items = Array.from(div.querySelectorAll('p, li'));
    if (!items.length) {
      items = Array.from(div.children);
    }
    if (!items.length) return;

    items.forEach(function(el) {
      var strong = el.querySelector('strong, b');
      if (!strong) return;
      var label = strong.textContent.replace(/[:\s]+$/, '').trim();
      var clone = el.cloneNode(true);
      clone.querySelectorAll('strong, b').forEach(function(s){ s.remove(); });
      var value = clone.textContent.replace(/^[\s:]+/, '').trim();
      if (label || value) rows.push({ label: label, value: value });
    });

    if (!rows.length) return;

    var table = document.createElement('table');
    table.className = 'hl-seller-info-table';
    rows.forEach(function(row) {
      var tr = document.createElement('tr');
      tr.innerHTML = '<td>' + row.label + '</td><td>' + row.value + '</td>';
      table.appendChild(tr);
    });

    div.innerHTML = '';
    div.appendChild(table);
    div.dataset.hlFormatted = 'true';
  }

  /* ===================================================================
     6) WEBKUL: SELLER-TITLE EINMALIG UMSCHREIBEN
     =================================================================== */
  var hlSellerTitleDone = false;
  function hlRewriteSellerTitle() {
    if (hlSellerTitleDone) return;
    var box = document.getElementById('wk_seller_detail_title');
    if (!box) return;
    var link = box.querySelector('a');
    if (!link) return;
    box.innerHTML =
      '<span class="hl-seller-label">Informationen über den Verkäufer auf diesem Link:</span> ' +
      '<a href="' + link.href + '">' + link.textContent + '</a>';
    hlSellerTitleDone = true;
  }

  /* ===================================================================
     7) REVIEWS / POLICY VERSCHIEBEN
     =================================================================== */
  function moveReviews() {
    var reviews = document.querySelector('.product-rating-section');
    var reviewsTab = document.querySelector('#reviews');
    if (reviews && reviewsTab && !reviewsTab.contains(reviews)) {
      reviewsTab.appendChild(reviews);
    }
  }

  function movePolicy() {
    var productPolicy = document.querySelector('.wk_prod_policy');
    var productTab = document.querySelector('#product_policy');
    var productPolicyTarget = document.querySelector('#seller-policy-tab');
    var productPolicyFallback = document.querySelector('.hl-policy-placeholder');

    if (!productTab || !productPolicyTarget || !productPolicyFallback) return;

    var policyText = productPolicy ? productPolicy.textContent.replace(/\s+/g, ' ').trim() : '';
    var looksLikeSellerPolicy =
      policyText.includes('Vertragspartner & Geltungsbereich') ||
      policyText.includes('Seller Policy') ||
      policyText.includes('Diese Seller Policy') ||
      policyText.includes('Versand & Lieferung');

    if (productPolicy && policyText !== '' && !looksLikeSellerPolicy) {
      if (!productPolicyTarget.contains(productPolicy)) {
        productPolicyTarget.appendChild(productPolicy);
      }
      productPolicyTarget.style.display = '';
      productPolicyFallback.style.display = 'none';
      return;
    }

    productPolicyTarget.style.display = 'none';
    productPolicyFallback.style.display = '';
  }

  /* ===================================================================
     8) UNTERE TABS
     =================================================================== */
  function hlSetupTabs() {
    var buttons = document.querySelectorAll('.hl-tab-btn');
    var panels  = document.querySelectorAll('.hl-tab-panel');
    if (!buttons.length) return;

    var defaultButton = document.querySelector('.hl-tab-btn[data-tab="longdesc"]');
    var defaultPanel  = document.getElementById('longdesc');

    buttons.forEach(function(b) { b.classList.remove('active'); });
    panels.forEach(function(p) { p.classList.remove('active'); });

    if (defaultButton && defaultPanel) {
      defaultButton.classList.add('active');
      defaultPanel.classList.add('active');
    }

    buttons.forEach(function(btn) {
      btn.addEventListener('click', function() {
        buttons.forEach(function(b) { b.classList.remove('active'); });
        panels.forEach(function(p) { p.classList.remove('active'); });

        btn.classList.add('active');

        var target = document.getElementById(btn.dataset.tab);
        if (target) target.classList.add('active');

        if (window.innerWidth <= 768) {
          /* OneCircle Runde 7 (Fix 2): scrollIntoView scrollte auch Body/
             Viewport horizontal mit (Seite rutschte nach links). Jetzt wird
             gezielt NUR die Tab-Leiste gescrollt. */
          var nav = btn.closest('.hl-tabs-nav');
          if (nav) {
            var target = btn.offsetLeft - (nav.clientWidth - btn.offsetWidth) / 2;
            nav.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
          }
        }

        /* Webkul Reviews neu initialisieren */
        if (btn.dataset.tab === 'reviews') {
          setTimeout(function() {
            if (typeof wk_rateit_init === 'function') wk_rateit_init();
            if (typeof wk_review_form_init === 'function') wk_review_form_init();
          }, 800);
        }
      });
    });
  }

  /* ===================================================================
     9) INIT + 1 GEBÜNDELTER, ZEITLICH BEGRENZTER OBSERVER
     =================================================================== */
  function hlSettle() {
    movePolicy();
    moveReviews();
    helalLifeFormatDeliveryDiv();
    hlRewriteSellerTitle();
    hlStripAll();
  }

  function hlInit() {
    var productSection = document.querySelector('[id^="MainProduct-"]');

    helalLifeBeautifyDescriptions(document);
    helalLifeRepairBrandLabels(document);
    helalLifeMergeProductCard(productSection);
    helalLifeMoveFacts();
    helalLifeShowReturnAddressInHinweise();
    hlSetupTabs();
    hlSettle();

    /* Ein Observer für Webkul-Nachladungen – debounced, schaltet sich selbst ab */
    var settleTimer = null;
    var bodyObserver = new MutationObserver(function() {
      if (settleTimer) return;
      settleTimer = setTimeout(function() {
        settleTimer = null;
        hlSettle();
      }, 200);
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });

    /* Scoped Attribut-Observer NUR für Webkuls Live-Farbinjektion in der Seller-Beschreibung */
    var sellerDesc = document.getElementById('wk_seller_description');
    var colorObserver = null;
    if (sellerDesc) {
      colorObserver = new MutationObserver(function() {
        if (hlIsStripping) return;
        helalLifeStripInlineColors('#wk_seller_description');
      });
      colorObserver.observe(sellerDesc, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'color', 'face']
      });
    }

    /* Heavy-Observer nach Settle-Phase abschalten (spart Akku/CPU auf Mobil) */
    setTimeout(function() { if (bodyObserver) bodyObserver.disconnect(); }, 15000);
    setTimeout(function() { if (colorObserver) colorObserver.disconnect(); }, 20000);

    /* Günstige Fallback-Durchläufe statt Dauer-Interval */
    [400, 1500, 4000].forEach(function(ms) { setTimeout(hlSettle, ms); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hlInit);
  } else {
    hlInit();
  }
})();